<template>
  <aside
    id="sidebar"
    class="bg-gray-100"
  >
    <SerialControl
      :disabled="disabled"
      :no-serial="noSerial"
      class="p-4 mt-lg-2 mb-1 mb-lg-4 bg-gray-100 box-shadow"
    />

    <InPageNavigationMobile
      :loading="getCurrentMachineLoading"
      :items="items"
    />
    <InPageNavigationDesktop
      :loading="getCurrentMachineLoading"
      :items="items"
    />
  </aside>
</template>

<script setup lang="ts">
import {computed} from 'vue'
import {useStore} from 'vuex'
import InPageNavigationDesktop from '@/components/ui/InPageNavigationDesktop.vue'
import InPageNavigationMobile from '@/components/ui/InPageNavigationMobile.vue'
import menuItems from '@/plugin/menuItems'
import SerialControl from './SerialControl.vue'

const $accessLevels: Record<string, string> = {
  FULL: 'Full access',
  LIMITED: 'Limited access',
  FORBIDDEN: 'Access forbidden'
}

withDefaults(defineProps<{
  disabled?: boolean
  noSerial?: boolean
}>(), {
  disabled: false,
  noSerial: false
})

const store = useStore()

const _getCurrentAbbreviation = computed(() => store.getters['mymachine/getCurrentAbbreviation'])
const getCurrentMachineError = computed(() => store.getters['mymachine/getCurrentMachineError'])
const getCurrentMachineLoading = computed(() => store.getters['mymachine/getCurrentMachineLoading'])
const getCurrentConnected = computed(() => store.getters['mymachine/getCurrentConnected'])
const getCurrentMachine = computed(() => store.getters['mymachine/getCurrentMachine'])
const regionalizedFeatures = computed(() => store.getters['mymachine/getRegionalizedFeatures'])
const getRegion = computed<string>(() => store.getters['mymachine/getRegion'])

const items = computed(() => {
  let _menuItems = menuItems.slice().filter((menuItem: any) => {
    if (!menuItem.region) return true
    return menuItem.region.includes(getRegion.value)
  })
  const error = getCurrentMachineError.value
  let disabledPages: string[] = []
  let lockedPages: string[] = []
  let hiddenPages: string[] = []

  // hide pages for ALAO
  if (getRegion.value === 'ALAO') {
    hiddenPages = ['controllerversions', 'faultcodes', 'locationHistory']
  }

  // check location history availability
  if (getCurrentMachine.value && !getCurrentMachine.value.location && getCurrentMachine.value.accessLevel === $accessLevels.LIMITED) lockedPages.push('locationHistory')

  // check machine not connected 404
  if (error && error.message && (error.message.startsWith('Machine with key') || error.message.startsWith('Equipment data not found')) || !getCurrentConnected.value) {
    disabledPages = disabledPages.concat(['controllerversions', 'faultcodes', 'locationHistory'])
  }

  // check machine not authorized 403
  if (error && error.code && error.code === 403) {
    lockedPages = lockedPages.concat(['controllerversions', 'faultcodes', 'locationHistory'])
  }

  // check feature flags
  const pageAccessfeatures = [
    {flagName: 'manualsAccess', pageName: 'manuals'},
    {flagName: 'schematicsAccess', pageName: 'schematics'},
    {flagName: 'fieldModsAccess', pageName: 'fieldmods'},
    {flagName: 'historyAccess', pageName: 'diagnostic'},
    {flagName: 'historyAccess', pageName: 'serviceHistory'},
    {flagName: 'faultCodesAccess', pageName: 'faultcodes'},
    {flagName: 'troubleshootingAccess', pageName: 'troubleshooting'}
  ]
  for (const feature of pageAccessfeatures) {
    if (!regionalizedFeatures.value[feature.flagName]) lockedPages.push(feature.pageName)
  }
  // map disabled pages
  if (disabledPages.length) {
    _menuItems = _menuItems.map((item: any) => disabledPages.includes(item.title.ck) ? {...item, disabled: true} : item)
  }
  if (lockedPages.length) {
    _menuItems = _menuItems.map((item: any) => lockedPages.includes(item.title.ck) ? {...item, disabled: true, icon: 'lock'} : item)
  }
  if (hiddenPages.length) {
    _menuItems = _menuItems.filter((item: any) => !hiddenPages.includes(item.title.ck))
  }
  return _menuItems
})
</script>

<style lang="scss" scoped>
  @import '../../scss/colors';
  @import '../../scss/grid';

  @include media-breakpoint-up('lg') {
    #sidebar {
      min-height: 100%;
      width: 21.5rem;
      background: $gray-100;
      align-items: center;
      justify-content: space-between;

      & > div {
        box-shadow: none;
      }
    }
  }
</style>
